import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { formatJvc, pickLocale } from '@juvo/utils';
import { store } from '../store.js';
import '../layouts/site-chrome.js';

interface PopulatedEnrollment {
  id: string;
  userId: string;
  cohortId: string;
  courseSlug: string;
  courseTitle?: { fr: string; en: string };
  courseTrack?: string;
  startDate?: string;
  endDate?: string;
  location?: string;
  status: 'enrolled' | 'waitlisted' | 'completed' | 'cancelled';
  paymentMethod: string;
  createdAt: string;
}

@customElement('jv-portal-page')
export class JvPortalPage extends LitElement {
  static styles = css`
    .layout {
      display: grid;
      gap: 1.5rem;
    }
    @media (min-width: 860px) {
      .layout {
        grid-template-columns: 14rem 1fr;
      }
    }
    nav {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    a {
      text-decoration: none;
      color: var(--jv-color-muted);
    }
    a[aria-current='page'] {
      color: var(--jv-color-accent);
      font-weight: 600;
    }
    h1 {
      margin: 0 0 1rem;
      font-size: var(--jv-fs-xl);
    }
    .badge-card {
      background: var(--jv-color-surface);
      border: var(--jv-border-w) solid var(--jv-color-border);
      padding: 1.25rem;
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
      margin-bottom: 1.5rem;
    }
    .geek-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      font-family: var(--jv-font-mono);
      font-size: 0.8rem;
      color: var(--jv-color-accent);
      background: rgba(15, 110, 86, 0.1);
      padding: 0.25rem 0.6rem;
      border-radius: 2px;
      width: fit-content;
    }
    .user-name {
      font-size: 1.5rem;
      font-weight: 700;
      margin: 0;
    }
    .user-meta {
      font-family: var(--jv-font-mono);
      font-size: 0.85rem;
      color: var(--jv-color-muted);
    }
    .grid-2 {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1rem;
      margin-bottom: 1.5rem;
    }
    @media (min-width: 600px) {
      .grid-2 {
        grid-template-columns: 1fr 1fr;
      }
    }
    .stat-card {
      background: var(--jv-color-surface);
      border: var(--jv-border-w) solid var(--jv-color-border);
      padding: 1.25rem;
    }
    .stat-title {
      font-family: var(--jv-font-mono);
      font-size: 0.8rem;
      text-transform: uppercase;
      color: var(--jv-color-muted);
      margin: 0 0 0.5rem;
    }
    .stat-num {
      font-family: var(--jv-font-display);
      font-size: 2.25rem;
      color: var(--jv-color-accent);
      margin: 0;
      line-height: 1;
    }
    .action-links {
      display: flex;
      gap: 0.75rem;
      margin-top: 1rem;
      font-size: 0.85rem;
    }
    .enrollments-grid {
      display: grid;
      gap: 1rem;
      margin-top: 1rem;
    }
    .enrollment-card {
      background: var(--jv-color-surface);
      border: var(--jv-border-w) solid var(--jv-color-border);
      padding: 1.25rem;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }
    .card-top {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 0.5rem;
    }
    .track-pill {
      font-family: var(--jv-font-mono);
      font-size: 0.75rem;
      text-transform: uppercase;
      padding: 0.15rem 0.5rem;
      background: var(--jv-color-bg);
      border: 1px solid var(--jv-color-border);
    }
    .status-pill {
      font-family: var(--jv-font-mono);
      font-size: 0.75rem;
      text-transform: uppercase;
      font-weight: 700;
      padding: 0.2rem 0.6rem;
    }
    .status-enrolled {
      background: #e6f4ea;
      color: #137333;
    }
    .status-waitlisted {
      background: #fef7e0;
      color: #b06000;
    }
    .status-completed {
      background: #e8f0fe;
      color: #1a73e8;
    }
    .dates {
      font-size: 0.85rem;
      color: var(--jv-color-muted);
      font-family: var(--jv-font-mono);
    }
  `;

  @property() section: 'dashboard' | 'courses' | 'contribute' | 'credits' | 'profile' = 'dashboard';
  @state() private enrollments: PopulatedEnrollment[] = [];
  @state() private contributionHours = '';
  @state() private contributionDesc = '';
  @state() private contributionSuccess = false;

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
    if (!store.user) {
      history.replaceState({}, '', '/login?next=/portal');
      window.dispatchEvent(new PopStateEvent('popstate'));
      return;
    }
    this.fetchData();
  }

  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }

  private unsubscribe?: () => void;

  private async fetchData() {
    try {
      const rows = await store.client.myEnrollments();
      this.enrollments = (rows as unknown as PopulatedEnrollment[]) || [];
    } catch {
      this.enrollments = [];
    }
  }

  render() {
    const t = store.t.bind(store);
    const user = store.user;
    if (!user) return html``;
    const links = [
      ['/portal', t('portal.dashboard'), 'dashboard'],
      ['/portal/courses', t('portal.courses'), 'courses'],
      ['/portal/credits', t('portal.credits'), 'credits'],
      ['/portal/contribute', t('portal.contribute'), 'contribute'],
      ['/portal/profile', t('portal.profile'), 'profile'],
    ] as const;
    return html`
      <jv-page>
        <div class="layout">
          <nav>
            ${links.map(
              ([href, label, key]) => html`
                <a href=${href} aria-current=${this.section === key ? 'page' : 'false'}>${label}</a>
              `,
            )}
          </nav>
          <section>${this.body(user.name, user.jvcBalance, user.email)}</section>
        </div>
      </jv-page>
    `;
  }

  private body(name: string, balance: number, email: string) {
    const t = store.t.bind(store);
    const lang = store.locale;

    switch (this.section) {
      case 'courses':
        return html`
          <h1>${t('portal.courses')}</h1>
          ${this.enrollments.length === 0
            ? html`
                <jv-empty-state heading=${t('portal.emptyCourses')}>
                  <p><a href="/courses">${t('portal.browseCourses')} →</a></p>
                </jv-empty-state>
              `
            : html`
                <div class="enrollments-grid">
                  ${this.enrollments.map((enr) => {
                    const title = enr.courseTitle
                      ? pickLocale(enr.courseTitle, lang)
                      : enr.courseSlug;
                    const statusClass =
                      enr.status === 'enrolled'
                        ? 'status-enrolled'
                        : enr.status === 'waitlisted'
                          ? 'status-waitlisted'
                          : 'status-completed';
                    const statusLabel =
                      enr.status === 'enrolled'
                        ? t('portal.statusEnrolled')
                        : enr.status === 'waitlisted'
                          ? t('portal.statusWaitlisted')
                          : t('portal.statusCompleted');
                    return html`
                      <div class="enrollment-card">
                        <div class="card-top">
                          <div>
                            <span class="track-pill">${enr.courseTrack || 'Academy'}</span>
                            <h3 style="margin: 0.35rem 0 0; font-size: 1.15rem;">${title}</h3>
                          </div>
                          <span class="status-pill ${statusClass}">${statusLabel}</span>
                        </div>
                        <div class="dates">
                          ${enr.startDate ? `📅 ${enr.startDate} → ${enr.endDate || ''}` : ''}
                          ${enr.location ? ` | 📍 ${enr.location}` : ''}
                        </div>
                        <div style="margin-top: 0.5rem;">
                          <a href="/courses/${enr.courseSlug}" style="font-size: 0.85rem; color: var(--jv-color-accent);">
                            View syllabus & schedule →
                          </a>
                        </div>
                      </div>
                    `;
                  })}
                </div>
              `}
        `;

      case 'credits':
        return html`
          <h1>${t('portal.credits')}</h1>
          <div class="grid-2">
            <div class="stat-card">
              <p class="stat-title">${t('portal.balance')}</p>
              <p class="stat-num">${formatJvc(balance)}</p>
              <div class="action-links">
                <a href="/merch" style="color: var(--jv-color-accent); font-weight: 500;">Spend at Juvo Store →</a>
              </div>
            </div>
            <div class="stat-card">
              <p class="stat-title">Verified Tasks</p>
              <p class="stat-num">${this.enrollments.length}</p>
              <div class="action-links">
                <a href="/portal/contribute" style="color: var(--jv-color-accent); font-weight: 500;">${t('portal.logContribution')} →</a>
              </div>
            </div>
          </div>
          <jv-card>
            <h3 style="margin:0 0 0.5rem;">Credit Earning System (JVC)</h3>
            <p style="color: var(--jv-color-muted); font-size: 0.9rem; line-height: 1.45;">
              Juvo Credits reward community service, repair work in the Faso Kanu workshop, hardware donations, and peer tutoring. Credits can be applied towards advanced cohort certifications or exchanged for parts and merchandise.
            </p>
          </jv-card>
        `;

      case 'contribute':
        return html`
          <h1>${t('portal.contribute')}</h1>
          <div class="badge-card">
            <h3 style="margin: 0 0 0.5rem;">${t('portal.logContribution')}</h3>
            <p style="margin: 0 0 1rem; color: var(--jv-color-muted); font-size: 0.9rem;">
              ${t('portal.contributeBody')}
            </p>
            ${this.contributionSuccess
              ? html`<p style="color: #137333; font-weight: 600;">✓ Contribution logged successfully. Staff will review and issue JVC.</p>`
              : html`
                  <form @submit=${this.onLogContribution} style="display: grid; gap: 0.75rem;">
                    <jv-form-field
                      label="Hours or Items"
                      required
                      .value=${this.contributionHours}
                      @jv-input=${(e: CustomEvent) => (this.contributionHours = e.detail.value)}
                    ></jv-form-field>
                    <jv-form-field
                      label="Description of Task (Workshop repair, mentoring, collection, etc.)"
                      required
                      multiline
                      .value=${this.contributionDesc}
                      @jv-input=${(e: CustomEvent) => (this.contributionDesc = e.detail.value)}
                    ></jv-form-field>
                    <div>
                      <jv-button type="submit" variant="primary">Submit for Verification</jv-button>
                    </div>
                  </form>
                `}
          </div>
        `;

      case 'profile':
        return html`
          <h1>${t('portal.profile')}</h1>
          <jv-card>
            <p><strong>Name:</strong> ${name}</p>
            <p><strong>Email:</strong> ${email}</p>
            <p><strong>Roles:</strong> ${store.user?.roles.join(', ')}</p>
            <p><strong>Primary Hub:</strong> Faso Kanu, Bamako</p>
          </jv-card>
        `;

      default:
        return html`
          <div class="badge-card">
            <div class="geek-badge">
              <span>●</span> ${t('portal.memberBadge')}
            </div>
            <h2 class="user-name">${t('portal.welcome')}, ${name}</h2>
            <div class="user-meta">${t('portal.hub')} • ${email}</div>
          </div>

          <div class="grid-2">
            <div class="stat-card">
              <p class="stat-title">${t('portal.balance')}</p>
              <p class="stat-num">${formatJvc(balance)}</p>
              <div class="action-links">
                <a href="/merch" style="color: var(--jv-color-accent); font-weight: 500;">Redeem at store →</a>
              </div>
            </div>
            <div class="stat-card">
              <p class="stat-title">Active Courses</p>
              <p class="stat-num">${this.enrollments.filter((e) => e.status === 'enrolled').length}</p>
              <div class="action-links">
                <a href="/courses" style="color: var(--jv-color-accent); font-weight: 500;">Browse catalog →</a>
              </div>
            </div>
          </div>

          <div style="display: flex; justify-content: space-between; align-items: baseline; margin-top: 1.5rem;">
            <h2 style="font-size: 1.25rem; margin: 0;">${t('portal.courses')}</h2>
            <a href="/courses" style="font-size: 0.85rem; color: var(--jv-color-accent);">${t('portal.browseCourses')} →</a>
          </div>

          ${this.enrollments.length === 0
            ? html`
                <jv-empty-state heading=${t('portal.emptyCourses')}>
                  <p><a href="/courses">${t('portal.browseCourses')} →</a></p>
                </jv-empty-state>
              `
            : html`
                <div class="enrollments-grid">
                  ${this.enrollments.slice(0, 3).map((enr) => {
                    const title = enr.courseTitle
                      ? pickLocale(enr.courseTitle, lang)
                      : enr.courseSlug;
                    const statusClass =
                      enr.status === 'enrolled'
                        ? 'status-enrolled'
                        : enr.status === 'waitlisted'
                          ? 'status-waitlisted'
                          : 'status-completed';
                    const statusLabel =
                      enr.status === 'enrolled'
                        ? t('portal.statusEnrolled')
                        : enr.status === 'waitlisted'
                          ? t('portal.statusWaitlisted')
                          : t('portal.statusCompleted');
                    return html`
                      <div class="enrollment-card">
                        <div class="card-top">
                          <h4 style="margin:0; font-size: 1.05rem;">${title}</h4>
                          <span class="status-pill ${statusClass}">${statusLabel}</span>
                        </div>
                        <div class="dates">
                          ${enr.startDate ? `📅 ${enr.startDate} → ${enr.endDate || ''}` : ''}
                          ${enr.location ? ` | 📍 ${enr.location}` : ''}
                        </div>
                      </div>
                    `;
                  })}
                </div>
              `}
        `;
    }
  }

  private onLogContribution = (e: Event) => {
    e.preventDefault();
    this.contributionSuccess = true;
    this.contributionHours = '';
    this.contributionDesc = '';
  };
}
